import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

const SoundContext = createContext(null);
export const useSound = () => useContext(SoundContext);

export function SoundProvider({ children }) {
  const [enabled, setEnabled] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const overdueRef = useRef(0);
  const ctxRef = useRef(null);
  const intervalRef = useRef(null);

  const ensureCtx = useCallback(() => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) ctxRef.current = new AC();
    }
    if (ctxRef.current && ctxRef.current.state === "suspended") ctxRef.current.resume();
    return ctxRef.current;
  }, []);

  const beep = useCallback((freq = 880, duration = 0.35) => {
    const ctx = ensureCtx();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = freq;
    gain.gain.value = volume * 0.25;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const t = ctx.currentTime;
    osc.start(t);
    gain.gain.setValueAtTime(volume * 0.25, t);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + duration);
    osc.stop(t + duration);
  }, [ensureCtx, volume]);

  const playAlert = useCallback(() => {
    beep(880, 0.25);
    setTimeout(() => beep(660, 0.3), 300);
  }, [beep]);

  const enable = useCallback(() => {
    ensureCtx();
    setEnabled(true);
    beep(1046, 0.2);
  }, [ensureCtx, beep]);

  const mute = useCallback(() => setEnabled(false), []);
  const testSound = useCallback(() => { ensureCtx(); playAlert(); }, [ensureCtx, playAlert]);

  const setOverdueCount = useCallback((n) => { overdueRef.current = n; }, []);

  useEffect(() => {
    if (enabled) {
      if (overdueRef.current > 0) playAlert();
      intervalRef.current = setInterval(() => {
        if (overdueRef.current > 0) playAlert();
      }, 10000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [enabled, playAlert]);

  return (
    <SoundContext.Provider value={{ enabled, volume, setVolume, enable, mute, testSound, setOverdueCount }}>
      {children}
    </SoundContext.Provider>
  );
}
